/**
 * Generated from platform/mapping-service/data/company_taxonomy.json.
 * Run platform/export/taxonomyconstantsexport.py to update.
 */

/**
 * Data-driven company mapping hierarchy.
 *
 * Canonical taxonomy shape: Industry -> Sub Industry -> Category -> Market.
 * Filter selections store Market labels, because exported table rows expose Market
 * as the leaf-level end market.
 */

export const TAXONOMY_VERSION = "2026-05";

// ---------------------------------------------------------------------------
// Hierarchy
// ---------------------------------------------------------------------------

export const CATEGORY_HIERARCHY = {
  "Software": {
    "Infrastructure Software": {
      "Data Platforms": [
        "Operational Databases",
        "Data Warehouses & Lakehouses",
        "Business Intelligence & Analytics",
        "Product Analytics",
        "Data Governance & Catalog",
        "Search & Retrieval"
      ],
      "DevOps & Platforms": [
        "DevSecOps & Delivery",
        "Cloud & Platform Ops",
        "Developer Platforms",
        "Incident Response",
        "Observability & APM",
        "AI Coding & Agents"
      ],
      "Integration & APIs": [
        "iPaaS & API Integration",
        "API Management & Middleware",
        "Data Integration & Orchestration"
      ],
      "Security & IT Management": [
        "Identity & Access Management",
        "Endpoint Security",
        "Endpoint & Device Management",
        "MSP IT Management & RMM",
        "Cloud Security & CNAPP",
        "Network Security & SASE",
        "Data Security & DSPM",
        "Backup & Recovery",
        "Exposure Management",
        "Security Operations & SIEM",
        "GRC, Privacy & Compliance",
        "Email & Human Risk"
      ],
      "Cloud & Edge": [
        "Hosting & Cloud Platforms",
        "CDN & Edge Delivery",
        "Networking"
      ],
      "AI Infrastructure": [
        "Foundation Models",
        "AI Data & Developer Platforms",
        "Model Serving & Inference",
        "Synthetic Media"
      ]
    },
    "Application Software": {
      "Workflow & Work Management": [
        "ITSM & ESM",
        "Project & Work Management",
        "Low-Code & Automation",
        "Search & Knowledge"
      ],
      "CRM & Customer Engagement": [
        "CRM & Sales Platforms",
        "Marketing Automation",
        "Support & Contact Center",
        "Customer Success & CX",
        "Communications APIs",
        "Social & Reputation Management",
        "Revenue Intelligence"
      ],
      "Productivity & Content": [
        "Collaboration & Messaging",
        "Content Management & File Sharing",
        "Creative & Design",
        "Events & Video",
        "Website & Digital Presence"
      ],
      "Back Office Software": [
        "HCM & Payroll",
        "Learning & Talent",
        "Finance, ERP & EPM",
        "Accounting Automation",
        "Procurement & Spend Management",
        "Tax & Compliance",
        "Contract Lifecycle Management",
        "Supply Chain & Logistics"
      ]
    },
    "Vertical Software": {
      "Healthcare": [
        "Life Sciences & Pharma",
        "Provider EHR & Clinical Systems",
        "Revenue Cycle & Healthcare Operations",
        "AI Clinical Documentation",
        "Medical Knowledge & Decision Support"
      ],
      "Financial Services": [
        "Banking & Lending",
        "Insurance",
        "Investment & Asset Management",
        "Capital Markets Workflow",
        "Financial Services Data & Analytics"
      ],
      "Construction & Property": [
        "Construction Management",
        "Property Management",
        "Field Service Management",
        "Home Services & Trades"
      ],
      "Industrial & Engineering": [
        "CAD, PLM & Design Engineering",
        "Simulation & EDA",
        "Industrial Operations & IoT",
        "Automotive & Autonomy"
      ],
      "Education & Public Sector": [
        "K-12 & Higher Education",
        "Government & Civic",
        "Public Safety & Justice"
      ],
      "Legal & Professional": [
        "Legal Practice Management",
        "Legal AI & Document Review",
        "Professional Services Operations"
      ],
      "Consumer & Nonprofit": [
        "Fitness, Wellness & Local Services",
        "Nonprofit & Association Management",
        "Hospitality & Leisure Operations"
      ]
    },
    "Diversified Software": {
      "Diversified Software": [
        "Global Technology Platform",
        "Diversified Enterprise Software",
        "Vertical Software Holding Company",
        "Multi-Segment Software Platform"
      ]
    }
  },
  "Payments & FinTech": {
    "Payments & Commerce": {
      "Payment Rails": [
        "Merchant Processing",
        "Payment Networks",
        "Wallets & Checkout",
        "Cross-Border Payments"
      ],
      "Commerce Software": [
        "Commerce & POS"
      ]
    },
    "B2B & Banking FinTech": {
      "B2B Payments": [
        "AP/AR Automation",
        "Spend Management"
      ],
      "Banking Infrastructure": [
        "Banking Connectivity",
        "Core Banking",
        "Fraud & Risk Infrastructure"
      ]
    }
  },
  "Information Services": {
    "Financial Information": {
      "Market Data & Indices": [
        "Market Data & Indices"
      ],
      "Credit, Ratings & Risk": [
        "Credit, Ratings & Risk"
      ]
    },
    "Business Information": {
      "Market Intelligence": [
        "Market Intelligence"
      ],
      "Research & Advisory": [
        "Research & Advisory"
      ]
    }
  }
};

export const INDUSTRY_ORDER = [
  "Software",
  "Payments & FinTech",
  "Information Services"
];

export const SUB_INDUSTRY_ORDER = {
  "Software": [
    "Infrastructure Software",
    "Application Software",
    "Vertical Software",
    "Diversified Software"
  ],
  "Payments & FinTech": [
    "Payments & Commerce",
    "B2B & Banking FinTech"
  ],
  "Information Services": [
    "Financial Information",
    "Business Information"
  ]
};

export const CATEGORY_ORDER = {
  "Software": {
    "Infrastructure Software": [
      "Data Platforms",
      "DevOps & Platforms",
      "Integration & APIs",
      "Security & IT Management",
      "Cloud & Edge",
      "AI Infrastructure"
    ],
    "Application Software": [
      "Workflow & Work Management",
      "CRM & Customer Engagement",
      "Productivity & Content",
      "Back Office Software"
    ],
    "Vertical Software": [
      "Healthcare",
      "Financial Services",
      "Construction & Property",
      "Industrial & Engineering",
      "Education & Public Sector",
      "Legal & Professional",
      "Consumer & Nonprofit"
    ],
    "Diversified Software": [
      "Diversified Software"
    ]
  },
  "Payments & FinTech": {
    "Payments & Commerce": [
      "Payment Rails",
      "Commerce Software"
    ],
    "B2B & Banking FinTech": [
      "B2B Payments",
      "Banking Infrastructure"
    ]
  },
  "Information Services": {
    "Financial Information": [
      "Market Data & Indices",
      "Credit, Ratings & Risk"
    ],
    "Business Information": [
      "Market Intelligence",
      "Research & Advisory"
    ]
  }
};
// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

const marketsForCategory = (industry, subIndustry, category) => (
  CATEGORY_HIERARCHY[industry]?.[subIndustry]?.[category] ?? []
);

export const getSubIndustryCategories = (industry, subIndustry) => (
  CATEGORY_ORDER[industry]?.[subIndustry]?.flatMap(category => marketsForCategory(industry, subIndustry, category)) ?? []
);

export const getCategoryMarkets = (industry, subIndustry, category) => (
  marketsForCategory(industry, subIndustry, category)
);

export const getIndustryCategories = (industry) => (
  SUB_INDUSTRY_ORDER[industry]?.flatMap(subIndustry => getSubIndustryCategories(industry, subIndustry)) ?? []
);

const _allCategories = INDUSTRY_ORDER.flatMap(industry => getIndustryCategories(industry));

const _ancestryMap = new Map();
for (const industry of INDUSTRY_ORDER) {
  for (const subIndustry of SUB_INDUSTRY_ORDER[industry]) {
    for (const category of CATEGORY_ORDER[industry][subIndustry]) {
      for (const market of marketsForCategory(industry, subIndustry, category)) {
        _ancestryMap.set(market, { industry, subIndustry, category, market });
      }
    }
  }
}

const _subIndustryToIndustry = new Map();
for (const industry of INDUSTRY_ORDER) {
  for (const subIndustry of SUB_INDUSTRY_ORDER[industry]) {
    _subIndustryToIndustry.set(subIndustry, industry);
  }
}

export const getAllCategories = () => [..._allCategories];

export const getCategoryAncestry = (market) => _ancestryMap.get(market) ?? null;

export const getSubIndustryIndustry = (subIndustry) => _subIndustryToIndustry.get(subIndustry) ?? null;

const _categoryToAncestry = new Map();
const _subIndustryToCategories = new Map();
const _industryToCategories = new Map();

for (const industry of INDUSTRY_ORDER) {
  const industryCategories = [];
  for (const subIndustry of SUB_INDUSTRY_ORDER[industry]) {
    const categoryLabels = CATEGORY_ORDER[industry]?.[subIndustry] ?? [];
    _subIndustryToCategories.set(subIndustry, categoryLabels);
    industryCategories.push(...categoryLabels);
    for (const category of categoryLabels) {
      _categoryToAncestry.set(category, { industry, subIndustry, category });
    }
  }
  _industryToCategories.set(industry, industryCategories);
}

export const CATEGORY_GRAINS = {
  INDUSTRY: 'industry',
  SUB_INDUSTRY: 'subIndustry',
  CATEGORY: 'category',
  MARKET: 'market',
};

export const CATEGORY_CHANGE_SOURCES = {
  USER: 'user',
  GRAIN_TOGGLE: 'grainToggle',
  GRAIN_NORMALIZE: 'grainNormalize',
  AUTO_COHORT: 'autoCohort',
  AVAILABLE_MARKETS_NORMALIZE: 'availableMarketsNormalize',
  CATEGORY_TABLE_LINK: 'categoryTableLink',
  CATEGORY_DRILLDOWN: 'categoryDrilldown',
};

const PERSISTENT_CATEGORY_CHANGE_SOURCES = new Set([
  CATEGORY_CHANGE_SOURCES.USER,
  CATEGORY_CHANGE_SOURCES.GRAIN_TOGGLE,
  CATEGORY_CHANGE_SOURCES.CATEGORY_TABLE_LINK,
  CATEGORY_CHANGE_SOURCES.CATEGORY_DRILLDOWN,
]);

export const shouldPersistCategoryChange = (source) => (
  source == null || PERSISTENT_CATEGORY_CHANGE_SOURCES.has(source)
);

export const getCategoryLabelType = (label) => {
  if (_ancestryMap.has(label)) return CATEGORY_GRAINS.MARKET;
  if (_categoryToAncestry.has(label)) return CATEGORY_GRAINS.CATEGORY;
  if (_subIndustryToIndustry.has(label)) return CATEGORY_GRAINS.SUB_INDUSTRY;
  if (INDUSTRY_ORDER.includes(label)) return CATEGORY_GRAINS.INDUSTRY;
  return null;
};

export const inferCategorySelectionGrain = (selection, fallback = CATEGORY_GRAINS.CATEGORY) => {
  const selectedList = Array.isArray(selection) ? selection.filter(Boolean) : [];
  const types = selectedList.map(getCategoryLabelType).filter(Boolean);

  if (types.includes(CATEGORY_GRAINS.MARKET)) return CATEGORY_GRAINS.MARKET;
  if (types.includes(CATEGORY_GRAINS.SUB_INDUSTRY)) return CATEGORY_GRAINS.SUB_INDUSTRY;
  if (types.includes(CATEGORY_GRAINS.INDUSTRY)) return CATEGORY_GRAINS.INDUSTRY;
  if (types.includes(CATEGORY_GRAINS.CATEGORY)) return CATEGORY_GRAINS.CATEGORY;
  return fallback;
};

export const getIndustryCategoryLabels = (industry) => (
  _industryToCategories.get(industry) ? [..._industryToCategories.get(industry)] : []
);

export const getIndustrySubIndustryLabels = (industry) => (
  SUB_INDUSTRY_ORDER[industry] ? [...SUB_INDUSTRY_ORDER[industry]] : []
);

export const getSubIndustryCategoryLabels = (industry, subIndustry) => {
  if (getSubIndustryIndustry(subIndustry) !== industry) return [];
  return _subIndustryToCategories.get(subIndustry) ? [..._subIndustryToCategories.get(subIndustry)] : [];
};

export const getAllCategoryLabels = () => (
  INDUSTRY_ORDER.flatMap(industry => getIndustryCategoryLabels(industry))
);

export const getAllSubIndustryLabels = () => (
  INDUSTRY_ORDER.flatMap(industry => getIndustrySubIndustryLabels(industry))
);

export const toMarketSet = (markets) => {
  if (markets == null) return null;
  if (markets instanceof Set) return markets;
  if (!Array.isArray(markets)) return null;
  return new Set(markets.filter(Boolean));
};

export const orderMarkets = (markets) => {
  const marketSet = toMarketSet(markets) ?? new Set(markets || []);
  return _allCategories.filter(market => marketSet.has(market));
};

const getVisibleMarkets = (markets, availableMarkets = null) => {
  const availableSet = toMarketSet(availableMarkets);
  return markets.filter(market => !availableSet || availableSet.has(market));
};

const getVisibleCategoryLabelsForScope = (industries, availableMarkets = null) => (
  industries.flatMap(industry => (
    SUB_INDUSTRY_ORDER[industry]?.flatMap(subIndustry => (
      getSubIndustryCategoryLabels(industry, subIndustry).filter(category =>
        getVisibleMarkets(getCategoryMarkets(industry, subIndustry, category), availableMarkets).length > 0
      )
    )) ?? []
  ))
);

const getVisibleSubIndustryLabelsForScope = (industries, availableMarkets = null) => (
  industries.flatMap(industry => (
    SUB_INDUSTRY_ORDER[industry]?.filter(subIndustry =>
      getVisibleMarkets(getSubIndustryCategories(industry, subIndustry), availableMarkets).length > 0
    ) ?? []
  ))
);

export const getSelectableLabelsForGrain = (
  grain,
  {
    availableMarkets = null,
    industries = INDUSTRY_ORDER,
  } = {}
) => {
  if (grain === CATEGORY_GRAINS.MARKET) {
    const availableSet = toMarketSet(availableMarkets);
    return orderMarkets(industries.flatMap(industry => getIndustryCategories(industry)))
      .filter(market => !availableSet || availableSet.has(market));
  }

  if (grain === CATEGORY_GRAINS.SUB_INDUSTRY) {
    return getVisibleSubIndustryLabelsForScope(industries, availableMarkets);
  }

  if (grain === CATEGORY_GRAINS.INDUSTRY) {
    return INDUSTRY_ORDER.filter(industry =>
      industries.includes(industry)
      && getVisibleMarkets(getIndustryCategories(industry), availableMarkets).length > 0
    );
  }

  if (grain === CATEGORY_GRAINS.CATEGORY) {
    return getVisibleCategoryLabelsForScope(industries, availableMarkets);
  }

  return [];
};

export const getBulkTargetForNode = (
  {
    grain,
    industry = null,
    subIndustry = null,
    category = null,
    market = null,
    availableMarkets = null,
  } = {}
) => {
  if (grain === CATEGORY_GRAINS.MARKET) {
    if (market) return getVisibleMarkets([market], availableMarkets);
    if (category) return getVisibleMarkets(getCategoryMarkets(industry, subIndustry, category), availableMarkets);
    if (subIndustry) return getVisibleMarkets(getSubIndustryCategories(industry, subIndustry), availableMarkets);
    if (industry) return getVisibleMarkets(getIndustryCategories(industry), availableMarkets);
    return [];
  }

  if (grain === CATEGORY_GRAINS.CATEGORY) {
    if (category) {
      return getVisibleMarkets(getCategoryMarkets(industry, subIndustry, category), availableMarkets).length > 0
        ? [category]
        : [];
    }
    if (subIndustry) {
      return getSubIndustryCategoryLabels(industry, subIndustry).filter(label =>
        getVisibleMarkets(getCategoryMarkets(industry, subIndustry, label), availableMarkets).length > 0
      );
    }
    if (industry) return getVisibleCategoryLabelsForScope([industry], availableMarkets);
    return [];
  }

  if (grain === CATEGORY_GRAINS.SUB_INDUSTRY) {
    if (subIndustry) {
      return getVisibleMarkets(getSubIndustryCategories(industry, subIndustry), availableMarkets).length > 0
        ? [subIndustry]
        : [];
    }
    if (industry) return getVisibleSubIndustryLabelsForScope([industry], availableMarkets);
  }

  if (grain === CATEGORY_GRAINS.INDUSTRY) {
    if (industry) {
      return getVisibleMarkets(getIndustryCategories(industry), availableMarkets).length > 0
        ? [industry]
        : [];
    }
  }

  return [];
};

export const sanitizeSelectionForGrain = (
  values,
  grain,
  {
    availableMarkets = null,
    industries = INDUSTRY_ORDER,
  } = {}
) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  const selectableSet = new Set(getSelectableLabelsForGrain(grain, { availableMarkets, industries }));
  const direct = values.filter(value => selectableSet.has(value));
  if (direct.length > 0) return [...new Set(direct)];

  return normalizeCategorySelection(values, availableMarkets, { grain, fallback: [] })
    .filter(value => selectableSet.has(value));
};

export const toggleCategorySelection = (currentValues, targetValues) => {
  const current = Array.isArray(currentValues) ? currentValues.filter(Boolean) : [];
  const targets = Array.isArray(targetValues) ? [...new Set(targetValues.filter(Boolean))] : [];
  if (targets.length === 0) return current;

  const targetSet = new Set(targets);
  const anySelected = current.some(value => targetSet.has(value));

  if (anySelected) return current.filter(value => !targetSet.has(value));
  return [...new Set([...current, ...targets])];
};

export const resolveSelectionToMarkets = (selection, availableMarkets = null) => {
  const selectedList = Array.isArray(selection) ? selection.filter(Boolean) : [];
  const availableSet = toMarketSet(availableMarkets);
  const markets = new Set();

  selectedList.forEach((label) => {
    const type = getCategoryLabelType(label);
    if (type === CATEGORY_GRAINS.MARKET) {
      markets.add(label);
      return;
    }

    if (type === CATEGORY_GRAINS.CATEGORY) {
      const ancestry = _categoryToAncestry.get(label);
      getCategoryMarkets(ancestry.industry, ancestry.subIndustry, label).forEach(market => markets.add(market));
      return;
    }

    if (type === CATEGORY_GRAINS.SUB_INDUSTRY) {
      const industry = getSubIndustryIndustry(label);
      getSubIndustryCategories(industry, label).forEach(market => markets.add(market));
      return;
    }

    if (type === CATEGORY_GRAINS.INDUSTRY) {
      getIndustryCategories(label).forEach(market => markets.add(market));
    }
  });

  const ordered = orderMarkets(markets);
  return availableSet
    ? ordered.filter(market => availableSet.has(market))
    : ordered;
};

export const resolveSelectionToMarketSet = (selection, availableMarkets = null) => (
  new Set(resolveSelectionToMarkets(selection, availableMarkets))
);

const getVisibleCategoryLabels = (availableMarkets = null) => {
  const availableSet = toMarketSet(availableMarkets);
  return getAllCategoryLabels().filter((category) => {
    const ancestry = _categoryToAncestry.get(category);
    const markets = getCategoryMarkets(ancestry.industry, ancestry.subIndustry, category);
    return !availableSet || markets.some(market => availableSet.has(market));
  });
};

export const selectionHasOnlyValidLabels = (selection) => (
  Array.isArray(selection) && selection.length > 0 && selection.every(label => getCategoryLabelType(label))
);

export const normalizeCategorySelection = (
  selected,
  availableMarkets,
  {
    fallback = null,
    grain = CATEGORY_GRAINS.CATEGORY,
  } = {}
) => {
  const fallbackSelection = Array.isArray(fallback) && fallback.length > 0
    ? fallback
    : getDefaultCategories();

  if (!Array.isArray(selected)) return fallbackSelection;
  if (selected.length === 0) return [];

  const resolvedMarkets = resolveSelectionToMarkets(selected, availableMarkets);
  if (resolvedMarkets.length === 0) return fallbackSelection;

  if (grain === CATEGORY_GRAINS.MARKET) return resolvedMarkets;
  if (grain === CATEGORY_GRAINS.SUB_INDUSTRY) {
    const subIndustries = SUB_INDUSTRY_ORDER[INDUSTRY_ORDER[0]]
      ? INDUSTRY_ORDER.flatMap(industry => SUB_INDUSTRY_ORDER[industry])
      : [];
    const marketSet = new Set(resolvedMarkets);
    const next = subIndustries.filter((subIndustry) => {
      const industry = getSubIndustryIndustry(subIndustry);
      return getSubIndustryCategories(industry, subIndustry).some(market => marketSet.has(market));
    });
    return next.length > 0 ? next : fallbackSelection;
  }
  if (grain === CATEGORY_GRAINS.INDUSTRY) {
    const marketSet = new Set(resolvedMarkets);
    const next = INDUSTRY_ORDER.filter(industry =>
      getIndustryCategories(industry).some(market => marketSet.has(market))
    );
    return next.length > 0 ? next : fallbackSelection;
  }

  const marketSet = new Set(resolvedMarkets);
  const categories = getVisibleCategoryLabels(availableMarkets).filter((category) => {
    const ancestry = _categoryToAncestry.get(category);
    return getCategoryMarkets(ancestry.industry, ancestry.subIndustry, category)
      .some(market => marketSet.has(market));
  });

  return categories.length > 0 ? categories : fallbackSelection;
};

export const getAvailableMarketsFromRows = (
  rows,
  { marketField = 'Market' } = {}
) => {
  if (!Array.isArray(rows)) return [];
  const markets = new Set();
  rows.forEach((row) => {
    const market = row?.[marketField];
    if (_ancestryMap.has(market)) markets.add(market);
  });
  return orderMarkets(markets);
};

export const getAvailableMarketsFromTickers = (tickers, tickerToMarket) => {
  if (!Array.isArray(tickers)) return [];
  const markets = new Set();
  tickers.forEach((item) => {
    const ticker = typeof item === 'string'
      ? item
      : item?.ticker || item?.Ticker || item?.vendorTicker || item?.['Company Symbol'];
    if (!ticker) return;
    const market = tickerToMarket?.get?.(String(ticker).toUpperCase());
    if (_ancestryMap.has(market)) markets.add(market);
  });
  return orderMarkets(markets);
};

export const pruneSelectedCategories = (
  selected,
  availableMarkets,
  { fallbackToAvailable = true, fallback = null, grain = null, restrictToAvailable = true } = {}
) => {
  if (Array.isArray(selected) && selected.length === 0) return [];

  const availableSet = toMarketSet(availableMarkets);
  const selectedList = selectionHasOnlyValidLabels(selected) ? selected : [];
  const targetGrain = grain ?? inferCategorySelectionGrain(selectedList, CATEGORY_GRAINS.CATEGORY);
  if (!availableSet || !restrictToAvailable) {
    return normalizeCategorySelection(selectedList, null, { fallback, grain: targetGrain });
  }

  const resolved = resolveSelectionToMarkets(selectedList, availableSet);
  const next = normalizeCategorySelection(resolved, availableSet, { fallback, grain: targetGrain });
  if (next.length > 0 || !fallbackToAvailable) return next;
  if (targetGrain === CATEGORY_GRAINS.MARKET) return orderMarkets(availableSet);
  return normalizeCategorySelection(orderMarkets(availableSet), availableSet, { fallback, grain: targetGrain });
};

export const getMarketCompanyCounts = (
  rows,
  {
    marketField = 'Market',
    companyField = 'Company Symbol',
  } = {}
) => {
  const companiesByMarket = new Map();
  if (!Array.isArray(rows)) return new Map();

  rows.forEach((row, index) => {
    const market = row?.[marketField];
    if (!_ancestryMap.has(market)) return;
    const company = row?.[companyField] || row?.Ticker || row?.ticker || `${market}-${index}`;
    if (!companiesByMarket.has(market)) companiesByMarket.set(market, new Set());
    companiesByMarket.get(market).add(String(company).toUpperCase());
  });

  return new Map(Array.from(companiesByMarket.entries()).map(([market, companies]) => [market, companies.size]));
};

const getMarketsForComparisonLevel = (targetMarket, level) => {
  const labelType = getCategoryLabelType(targetMarket);
  const ancestry = labelType === CATEGORY_GRAINS.MARKET
    ? getCategoryAncestry(targetMarket)
    : _categoryToAncestry.get(targetMarket);
  if (!ancestry) return [];

  if (level === 'market') return labelType === CATEGORY_GRAINS.MARKET ? [targetMarket] : getCategoryMarkets(ancestry.industry, ancestry.subIndustry, ancestry.category);
  if (level === 'category') return getCategoryMarkets(ancestry.industry, ancestry.subIndustry, ancestry.category);
  if (level === 'subIndustry') return getSubIndustryCategories(ancestry.industry, ancestry.subIndustry);
  if (level === 'industry') return getIndustryCategories(ancestry.industry);
  return getAllCategories();
};

export const resolveComparisonCategorySelection = ({
  targetMarket,
  rows,
  availableMarkets = null,
  minCompanyCount = 8,
  marketField = 'Market',
  companyField = 'Company Symbol',
  returnGrain = CATEGORY_GRAINS.CATEGORY,
} = {}) => {
  const available = toMarketSet(availableMarkets)
    ?? new Set(getAvailableMarketsFromRows(rows, { marketField }));
  const counts = getMarketCompanyCounts(rows, { marketField, companyField });
  const levels = ['category', 'subIndustry', 'industry', 'all'];

  for (const level of levels) {
    const markets = orderMarkets(
      getMarketsForComparisonLevel(targetMarket, level).filter(market => available.has(market))
    );
    if (!markets.length) continue;

    const companyCount = markets.reduce((sum, market) => sum + (counts.get(market) || 0), 0);
    if (companyCount >= minCompanyCount || level === 'all') {
      return normalizeCategorySelection(markets, available, { grain: returnGrain });
    }
  }

  return normalizeCategorySelection(orderMarkets(available), available, { grain: returnGrain });
};

export const getDefaultCategories = () => {
  return getIndustryCategoryLabels('Software');
};

export const getDefaultSubIndustries = () => {
  return getIndustrySubIndustryLabels('Software');
};

export const mapToGroupedCategory = (selectedCategory) => {
  if (!selectedCategory || selectedCategory.length === 0) return 'Software';

  const selected = resolveSelectionToMarketSet(selectedCategory);
  const hasIndustry = (industry) => getIndustryCategories(industry).some(market => selected.has(market));
  const hasSubIndustry = (industry, subIndustry) =>
    getSubIndustryCategories(industry, subIndustry).some(market => selected.has(market));

  const hasApp = hasSubIndustry('Software', 'Application Software');
  const hasInfra = hasSubIndustry('Software', 'Infrastructure Software');
  const hasVert = hasSubIndustry('Software', 'Vertical Software');
  const hasDiv = hasSubIndustry('Software', 'Diversified Software');
  const hasPayments = hasIndustry('Payments & FinTech');
  const hasData = hasIndustry('Information Services');

  const groups = [hasApp, hasInfra, hasVert, hasDiv, hasPayments, hasData];
  const groupCount = groups.filter(Boolean).length;

  if (groupCount > 1 || groupCount === 0) return 'Software';
  if (hasApp) return 'Application Software';
  if (hasInfra) return 'Infrastructure Software';
  if (hasVert) return 'Vertical Software';
  if (hasDiv) return 'Diversified Software';
  if (hasPayments) return 'Payments';
  if (hasData) return 'Information Services';
  return 'Software';
};
